import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject } from '@angular/core';

import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';

import { SpinnerService } from '../../services/spinner.service';
import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { Facility } from '../../firestore-models/facility';
import { ApiFacilityUser, ApiSetFacilityUsersRequest } from '../../api-client-module';
import { SelectFacilitiesDialogComponent } from '../select-facilities-dialog/select-facilities-dialog.component';
import { TranslateService } from '@ngx-translate/core';

export interface UserDialogData {
  change_type: number;
  user?: ApiFacilityUser;
}

@Component({
  selector: 'app-facility-user-dialog',
  templateUrl: './facility-user-dialog.component.html',
  styleUrls: ['./facility-user-dialog.component.css'],
  standalone: false,
})
export class FacilityUserDialogComponent implements OnInit {

  user?: ApiFacilityUser;
  isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;
  facilities: Facility[] = [];

  userFormGroup!: FormGroup;
  uidControl!: FormControl;
  nameControl!: FormControl;
  descriptionControl!: FormControl;
  emailControl!: FormControl;

  projectBaseLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<FacilityUserDialogComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: UserDialogData,
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.user = this.data.user;
    if (!this.user) {
      this.user = {
        project_id: this.configurationService.getProjectId(),
        facility_ids: [],
        uid: '',
        name: '',
        description: '',
        email: '',
        enabled: true
      };
      this.isAdded = true;
    }
    this.createForm();
    this.uidControl = this.userFormGroup.get('uid') as FormControl;
    this.nameControl = this.userFormGroup.get('name') as FormControl;
    this.descriptionControl = this.userFormGroup.get('description') as FormControl;
    this.emailControl = this.userFormGroup.get('email') as FormControl;
    this.getFacilities();
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  public getErrorMessageToName() {
    return this.nameControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToDescription() {
    return this.descriptionControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToEmail() {
    return this.emailControl.hasError('required')
    ? this.translate.instant('必須項目')
    : this.emailControl.hasError('email')
    ? this.translate.instant('メールアドレス')
    : '';
  }

  private createForm() {
    this.userFormGroup = this.formBuilder.group(
      {
        uid: [this.user!.uid, []],
        name: [this.user!.name, [Validators.required]],
        description: [this.user!.description, [Validators.required]],
        email: [this.user!.email, [Validators.required, Validators.email]],
      }
    );
  }

  addUser(): void {
    if (this.userFormGroup.valid) {
      this.user!.uid = this.uidControl.value;
      this.user!.name = this.nameControl.value;
      this.user!.description = this.descriptionControl.value;
      this.user!.email = this.emailControl.value;
      if (this.user) {
        let request: ApiSetFacilityUsersRequest = {
          access_token: '',
          change_type: this.data.change_type,
          users: [this.user]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setFacilityUsers(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じメールアドレスが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  //施設職員に紐付く施設情報設定
  private getFacilities() {
    this.facilities = [];
    if (!!this.user!.facility_ids && this.user!.facility_ids.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトの施設情報を全て取得してから
      //       施設職員情報に登録されている id で絞り込む。
      this.angularFirestore.collection<Facility>(
        AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.configurationService.getProjectId())
        ).valueChanges({ idField: 'id' }).subscribe(docs => {
          this.facilities = [];
          if (!!docs) {
            for (const facilityId of this.user!.facility_ids!) {
              for (const facility of docs) {
                if (facilityId === facility.id) {
                  this.facilities.push(facility);
                  break;
                }
              }
            }
          }
      });
    }
  }

  //施設選択ダイアログ
  openSelectFacilitiesDialog(): void {
    const dialogRef = this.dialog.open(SelectFacilitiesDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { exhibit: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        if (!this.user!.facility_ids) {
          this.user!.facility_ids = new Array<string>();
        }
        let isChanged: boolean = false;
        for (const row of result) {
          if (!this.user!.facility_ids.includes(row.id)) {
            this.user!.facility_ids.push(row.id);
            isChanged = true;
          }
        }
        if (!!isChanged) {
          this.getFacilities();
        }
      }
    });
  }

  //施設選択リストの削除
  deleteFacility(id: string): void {
    if (!!this.user!.facility_ids!.includes(id)) {
      this.user!.facility_ids = this.user!.facility_ids!.filter(value => value !== id);
      this.getFacilities();
    }
  }
}
